import type { Block, Span } from './compiler/types.ts';

/**
 * Minimal HTML → rich-text blocks for feed bodies (GOV.UK govspeak, the
 * Guardian's article body). Keeps paragraphs, headings (as bold
 * paragraphs), lists, bold and links; drops figures, asides, scripts,
 * tables and everything else's tags while keeping their text.
 */
const ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', pound: '£', euro: '€', copy: '©', reg: '®',
  hellip: '…', mdash: '—', ndash: '–', lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”', deg: '°', frac12: '½', frac14: '¼', frac34: '¾',
};

export function decodeEntities(s: string): string {
  return s.replace(/&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z]+);/g, (m, e: string) => {
    if (e[0] === '#') {
      const code = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : m;
    }
    return ENTITIES[e] ?? m;
  });
}

const DROP = new Set(['figure', 'aside', 'script', 'style', 'table', 'iframe', 'noscript', 'svg', 'figcaption', 'nav', 'footer']);
const BLOCK_TAGS = new Set(['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'blockquote', 'section', 'article']);

export function htmlToBlocks(html: string): Block[] {
  const blocks: Block[] = [];
  let spans: Span[] = [];
  let text = '';
  let bold = 0;
  let heading = 0;
  let href: string | undefined;
  let dropDepth = 0;
  let listDepth = 0;
  let listItems: Span[][] = [];
  let inItem = false;

  const flushText = () => {
    if (!text) return;
    const style: Span = { text };
    if (bold > 0 || heading > 0) style.bold = true;
    if (href) style.href = href;
    spans.push(style);
    text = '';
  };
  const endParagraph = () => {
    flushText();
    const cleaned = normalise(spans);
    if (cleaned.length) {
      if (inItem) listItems.push(cleaned);
      else blocks.push({ type: 'paragraph', spans: cleaned });
    }
    spans = [];
  };
  const endList = () => {
    endParagraph();
    inItem = false;
    if (listItems.length) blocks.push({ type: 'list', items: listItems });
    listItems = [];
  };

  const re = /<!--[\s\S]*?-->|<\/?([a-zA-Z][a-zA-Z0-9]*)([^>]*)>|([^<]+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html))) {
    if (m[0].startsWith('<!--')) continue;
    if (m[3] !== undefined) {
      if (dropDepth === 0) text += decodeEntities(m[3]).replace(/\s+/g, ' ');
      continue;
    }
    const tag = m[1]!.toLowerCase();
    const closing = m[0].startsWith('</');
    const attrs = m[2] ?? '';
    if (DROP.has(tag)) {
      if (closing) dropDepth = Math.max(0, dropDepth - 1);
      else if (!attrs.endsWith('/')) dropDepth++;
      continue;
    }
    if (dropDepth > 0) continue;
    switch (tag) {
      case 'br':
        text += ' ';
        break;
      case 'strong':
      case 'b':
        flushText();
        bold += closing ? -1 : 1;
        if (bold < 0) bold = 0;
        break;
      case 'a':
        flushText();
        if (closing) href = undefined;
        else {
          const h = /href\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(attrs);
          const url = h ? (h[2] ?? h[3] ?? h[4] ?? '') : '';
          href = /^https?:\/\//i.test(url) ? decodeEntities(url) : undefined;
        }
        break;
      case 'ul':
      case 'ol':
        if (closing) { listDepth = Math.max(0, listDepth - 1); if (listDepth === 0) endList(); }
        else { endParagraph(); listDepth++; }
        break;
      case 'li':
        endParagraph();
        inItem = !closing && listDepth > 0;
        break;
      default:
        if (BLOCK_TAGS.has(tag)) {
          endParagraph();
          if (/^h[1-6]$/.test(tag)) { heading += closing ? -1 : 1; if (heading < 0) heading = 0; }
        }
    }
  }
  endList();
  return blocks;
}

/** Trim, merge adjacent same-style spans and drop empties. */
function normalise(spans: Span[]): Span[] {
  const out: Span[] = [];
  for (const s of spans) {
    const prev = out[out.length - 1];
    if (prev && !!prev.bold === !!s.bold && (prev.href ?? '') === (s.href ?? '')) prev.text += s.text;
    else out.push({ ...s });
  }
  if (out[0]) out[0].text = out[0].text.replace(/^\s+/, '');
  const last = out[out.length - 1];
  if (last) last.text = last.text.replace(/\s+$/, '');
  return out.filter((s) => s.text.trim().length > 0 || (s.text.length > 0 && out.length > 1));
}

/** Plain text from HTML — for standfirsts that arrive with markup. */
export function htmlToText(html: string): string {
  return decodeEntities(html.replace(/<\/?(?:p|div|br|li|ul|ol|h[1-6]|tr|td|th|blockquote)\b[^>]*>/gi, ' ').replace(/<[^>]*>/g, '')).replace(/\s+/g, ' ').trim();
}
